import { z } from 'zod';

// src/schema/focus-view.ts
var FOCUS_VIEWS = [
  "qubidove_v",
  "qubidovelin_v",
  "qubidoco_v",
  "qubidocolin_v",
  "qubifave_v",
  "qubifaco_v",
  "qubicave_v",
  "qubicaco_v",
  "qubiarti_v",
  "qubicuen_v",
  "qubicomb_v",
  "qubifam2_v",
  "qubifam3_v",
  "qubifam4_v",
  "qubifam5_v",
  "qubialmolin_v",
  "qubialmoacu_v"
];
var FocusViewSchema = z.enum(FOCUS_VIEWS);
var LocaleStringSchema = z.object({
  es: z.string().min(1),
  en: z.string().min(1).optional(),
  ca: z.string().min(1).optional()
}).strict();
var ParameterTypeSchema = z.enum(["date", "number", "string", "ref", "boolean"]);
var DynamicExpressionSchema = z.string().regex(/^\$/);
var ParameterCommonSchema = z.object({
  name: z.string().min(1).regex(/^[a-z][a-z0-9_]*$/, "snake_case con lowercase y d\xEDgitos"),
  required: z.boolean().optional().default(false),
  label: LocaleStringSchema.optional()
});
var DateParameterSchema = ParameterCommonSchema.extend({
  type: z.literal("date"),
  default: z.union([z.string(), DynamicExpressionSchema]).optional()
});
var NumberParameterSchema = ParameterCommonSchema.extend({
  type: z.literal("number"),
  default: z.union([z.number(), DynamicExpressionSchema]).optional(),
  validation: z.object({
    min: z.number().optional(),
    max: z.number().optional()
  }).optional()
});
var StringParameterSchema = ParameterCommonSchema.extend({
  type: z.literal("string"),
  default: z.union([z.string(), DynamicExpressionSchema]).optional()
});
var BooleanParameterSchema = ParameterCommonSchema.extend({
  type: z.literal("boolean"),
  default: z.boolean().optional()
});
var RefParameterSchema = ParameterCommonSchema.extend({
  type: z.literal("ref"),
  ref_view: FocusViewSchema,
  ref_label_field: z.string().min(1),
  ref_value_field: z.string().min(1),
  multiple: z.boolean().optional().default(false),
  default: z.union([z.string(), z.array(z.string()), DynamicExpressionSchema]).optional()
}).passthrough();
var ParameterDefSchema = z.discriminatedUnion("type", [
  DateParameterSchema,
  NumberParameterSchema,
  StringParameterSchema,
  BooleanParameterSchema,
  RefParameterSchema
]);
var FilterOpSchema = z.enum([
  "eq",
  "between",
  "gte",
  "lte",
  "numeric_range",
  "starts_with",
  "ends_with",
  "contains",
  "in"
]);
var InitFilterSchema = z.object({
  field: z.string().min(1),
  value: z.union([z.string(), z.number()])
}).strict();
var FilterMappingSchema = z.object({
  param: z.string().min(1),
  field: z.string().min(1),
  op: FilterOpSchema,
  /** Para `op: 'between'`: nombre del parámetro que aporta el extremo superior. */
  with: z.string().optional(),
  /** Expresión booleana que decide si aplicar el filtro (eval cliente). */
  when: z.string().optional(),
  /** Opcionalmente, semi-join name a usar para inyectar el filtro (D-007). */
  via_semi_join: z.string().optional()
}).strict();
var FilterClauseSchema = z.discriminatedUnion("op", [
  z.object({ op: z.literal("eq"), field: z.string(), value: z.union([z.string(), z.number()]) }).strict(),
  z.object({
    op: z.literal("between"),
    field: z.string(),
    from: z.string(),
    to: z.string()
  }).strict(),
  z.object({ op: z.literal("gte"), field: z.string(), value: z.string() }).strict(),
  z.object({ op: z.literal("lte"), field: z.string(), value: z.string() }).strict(),
  z.object({
    op: z.literal("numeric_range"),
    field: z.string(),
    from: z.number(),
    to: z.number()
  }).strict(),
  z.object({ op: z.literal("starts_with"), field: z.string(), value: z.string() }).strict(),
  z.object({ op: z.literal("ends_with"), field: z.string(), value: z.string() }).strict(),
  z.object({ op: z.literal("contains"), field: z.string(), value: z.string() }).strict(),
  z.object({
    op: z.literal("in"),
    field: z.string(),
    values: z.array(z.union([z.string(), z.number()])).min(1)
  }).strict()
]);
var ViewIncludeSchema = z.object({
  view: FocusViewSchema,
  local: z.string().min(1),
  remote: z.string().min(1),
  alias: z.string().min(1).regex(/^[a-z][a-z0-9_]*$/, "snake_case").optional()
}).strict();
var SemiJoinModeSchema = z.enum(["in", "fanout", "prefix", "exclude_postfilter"]);
var SemiJoinFilterMappingSchema = z.object({
  param: z.string().min(1),
  field: z.string().min(1),
  op: FilterOpSchema,
  with: z.string().optional()
}).strict();
var SemiJoinDefSchema = z.object({
  name: z.string().min(1).regex(/^[a-z][a-z0-9_]*$/, "snake_case"),
  view: FocusViewSchema,
  project_field: z.string().min(1),
  inject_into: z.string().min(1),
  static_init: z.array(InitFilterSchema).optional(),
  mode: SemiJoinModeSchema.default("fanout"),
  max_projected_ids: z.number().int().min(1).max(500).default(500),
  max_in_size: z.number().int().min(1).max(500).default(50),
  max_concurrent_lookups: z.number().int().min(1).max(50).default(25),
  cache_ttl_seconds: z.number().int().min(0).default(300),
  /**
   * Optional filter mappings for the semi-join pre-query.
   * When present, replaces the legacy hardcoded date_from/date_to/fecha behaviour.
   * Each entry maps a resolved param to a filter field via an operator.
   */
  pre_query_filter_mapping: z.array(SemiJoinFilterMappingSchema).optional()
}).passthrough();
var ColumnTypeSchema = z.enum([
  "string",
  "number",
  "integer",
  "date",
  "datetime",
  "month",
  "money",
  "percent",
  "boolean"
]);
var AggregationFnSchema = z.enum(["sum", "avg", "min", "max", "count"]);
var ColumnDefSchema = z.object({
  key: z.string().min(1),
  type: ColumnTypeSchema,
  label: LocaleStringSchema.optional(),
  /** Sólo para `type: 'money'`. */
  currency: z.string().length(3).optional(),
  /** Sólo para `number` / `integer` / `money` / `percent`. */
  decimals: z.number().int().min(0).max(6).optional(),
  /** Expresión derivada (`$truncMonth(fecha)`, `$subtract(a, b)`, etc.). */
  derive: z.string().optional(),
  /** Funciones de agregación permitidas para esta columna. */
  aggregations: z.array(AggregationFnSchema).optional(),
  sortable: z.boolean().optional(),
  filterable: z.boolean().optional(),
  groupable: z.boolean().optional()
}).strict();
var VisualizationKindSchema = z.enum([
  "table",
  "bar",
  "line",
  "area",
  "pie",
  "donut",
  "kpi",
  "gauge",
  "scatter",
  "heatmap"
]);
var SortClauseSchema = z.object({
  column: z.string().min(1),
  dir: z.enum(["asc", "desc"])
}).strict();
var MetricSchema = z.object({
  column: z.string().min(1),
  agg: AggregationFnSchema
}).strict();
var ViewStateSchema = z.object({
  visualization: VisualizationKindSchema,
  group_by: z.array(z.string()).optional(),
  metrics: z.array(MetricSchema).optional(),
  sort: z.array(SortClauseSchema).optional(),
  page_size: z.number().int().min(1).max(500).optional()
}).strict();
var PaginationStrategySchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("page_number"),
    page_size: z.number().int().min(1).max(500)
  }).strict(),
  z.object({
    mode: z.literal("limit"),
    limit: z.number().int().min(1).max(500).optional()
  }).strict(),
  z.object({ mode: z.literal("none") }).strict()
]);
var FocusViewDataSourceSchema = z.object({
  type: z.literal("focus_view"),
  view: FocusViewSchema,
  includes: z.array(ViewIncludeSchema).optional(),
  max_rows: z.number().int().min(0).default(5e4),
  pagination: PaginationStrategySchema,
  default_order: z.string().optional()
}).strict();
var WidgetPositionSchema = z.object({
  x: z.number().int().min(0),
  y: z.number().int().min(0),
  w: z.number().int().min(1),
  h: z.number().int().min(1)
}).strict();
var DashboardWidgetSchema = z.object({
  id: z.string().min(1).regex(/^[a-z][a-z0-9_]*$/, "snake_case"),
  template_code: z.string().min(1),
  template_version: z.number().int().min(1),
  position: WidgetPositionSchema,
  title: LocaleStringSchema.optional(),
  visualization: VisualizationKindSchema,
  viz_config: z.record(z.unknown()).optional(),
  fixed_params: z.record(z.unknown()).optional()
}).strict();
var DashboardLayoutSchema = z.object({
  columns: z.number().int().min(1).max(24).default(12),
  auto_refresh_seconds: z.number().int().min(0).optional(),
  global_parameters: z.array(ParameterDefSchema).default([]),
  widgets: z.array(DashboardWidgetSchema).min(1)
}).strict();
var TemplateCommonSchema = z.object({
  code: z.string().min(1).regex(/^[a-z][a-z0-9_]*$/, "snake_case"),
  version: z.number().int().min(1),
  catalog_version: z.string().optional(),
  category: z.string().optional(),
  title: LocaleStringSchema,
  description: LocaleStringSchema.optional(),
  cache_ttl_seconds: z.number().int().min(0).default(300)
});
var InteractionsSchema = z.object({
  drilldown: z.object({
    from: z.string().min(1),
    to_template: z.string().min(1),
    map_params: z.record(z.string()).optional()
  }).strict().optional(),
  export: z.array(z.enum(["csv", "xlsx", "pdf"])).optional(),
  saved_views: z.object({
    allow_personal: z.boolean().default(true),
    allow_shared: z.boolean().default(true)
  }).strict().optional()
}).strict().optional();
var ReportTemplateSchema = TemplateCommonSchema.extend({
  kind: z.literal("report"),
  data_source: FocusViewDataSourceSchema,
  init_filters: z.array(InitFilterSchema).optional(),
  semi_joins: z.array(SemiJoinDefSchema).optional(),
  parameters: z.array(ParameterDefSchema).default([]),
  filter_mapping: z.array(FilterMappingSchema).default([]),
  columns: z.array(ColumnDefSchema).min(1),
  default_view: ViewStateSchema.optional(),
  interactions: InteractionsSchema
}).passthrough();
var DashboardTemplateSchema = TemplateCommonSchema.extend({
  kind: z.literal("dashboard"),
  data_source: FocusViewDataSourceSchema,
  parameters: z.array(ParameterDefSchema).default([]),
  filter_mapping: z.array(FilterMappingSchema).default([]),
  layout: DashboardLayoutSchema
}).passthrough();
var ReportTemplateModelSchema = z.discriminatedUnion("kind", [
  ReportTemplateSchema,
  DashboardTemplateSchema
]);

// src/query/build-query.ts
function buildApiV1Query(parts) {
  const segments = [];
  if (parts.pk_lookup !== void 0) {
    segments.push(`init=${parts.pk_lookup.field}:${encodeValue(parts.pk_lookup.value)}`);
  }
  const filterPieces = [];
  if (parts.forced_filters !== void 0) {
    for (const f of parts.forced_filters) {
      filterPieces.push(`${f.field}:=${encodeValue(f.value)}`);
    }
  }
  if (parts.filters !== void 0) {
    for (const f of parts.filters) {
      filterPieces.push(serializeFilterClause(f));
    }
  }
  if (filterPieces.length > 0) {
    segments.push(`filter=${filterPieces.join(",")}`);
  }
  if (parts.includes !== void 0 && parts.includes.length > 0) {
    const includePieces = parts.includes.map((i) => `${i.view}(${i.local}:${i.remote})`);
    segments.push(`include=${includePieces.join(",")}`);
  }
  if (parts.select !== void 0 && parts.select.length > 0) {
    segments.push(`select=${parts.select.join(",")}`);
  }
  if (parts.order !== void 0 && parts.order.trim() !== "") {
    segments.push(`order=${encodeURIComponent(parts.order)}`);
  }
  if (parts.pagination !== void 0) {
    const pagSeg = serializePagination(parts.pagination);
    if (pagSeg !== "") segments.push(pagSeg);
  }
  if (segments.length === 0) return "";
  return `?${segments.join("&")}`;
}
function serializeFilterClause(clause) {
  switch (clause.op) {
    case "eq":
      return `${clause.field}:${encodeValue(clause.value)}`;
    case "between":
      return `${clause.field}:${clause.from}..${clause.to}`;
    case "gte":
      return `${clause.field}:${clause.value}..`;
    case "lte":
      return `${clause.field}:..${clause.value}`;
    case "numeric_range":
      return `${clause.field}:${clause.from}..${clause.to}`;
    case "starts_with":
      return `${clause.field}:${encodeValue(clause.value)}*`;
    case "ends_with":
      return `${clause.field}:*${encodeValue(clause.value)}`;
    case "contains":
      return `${clause.field}:*${encodeValue(clause.value)}*`;
    case "in": {
      if (clause.values.length === 0) {
        throw new Error(`Empty values[] for in operator on field=${clause.field}`);
      }
      if (clause.values.length === 1) {
        const v = clause.values[0];
        if (v === void 0)
          throw new Error(`Empty values[] for in operator on field=${clause.field}`);
        return `${clause.field}:${encodeValue(v)}`;
      }
      return `${clause.field}:${clause.values.map((v) => encodeValue(v)).join("%2B")}`;
    }
  }
}
function serializePagination(p) {
  switch (p.mode) {
    case "page_number":
      return `page-number=${p.page_number}&page-size=${p.page_size}`;
    case "limit":
      return `limit=${p.limit}`;
    case "none":
      return "";
  }
}
function encodeValue(value) {
  if (typeof value === "number") return String(value);
  return encodeURIComponent(value);
}

// src/query/format-date.ts
function formatV1Date(input) {
  if (typeof input === "string") {
    const iso = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso !== null) return `${iso[3]}-${iso[2]}-${iso[1]}`;
    const dmy = input.match(/^(\d{2})-(\d{2})-(\d{4})/);
    if (dmy !== null) return input.slice(0, 10);
    throw new Error(`formatV1Date: formato no reconocido (${input})`);
  }
  const yyyy = input.getFullYear().toString().padStart(4, "0");
  const mm = (input.getMonth() + 1).toString().padStart(2, "0");
  const dd = input.getDate().toString().padStart(2, "0");
  return `${dd}-${mm}-${yyyy}`;
}
function parseV1Date(input) {
  const match = input.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (match === null) {
    throw new Error(`parseV1Date: se esperaba DD-MM-YYYY (recibido "${input}")`);
  }
  const dd = Number(match[1]);
  const mm = Number(match[2]);
  const yyyy = Number(match[3]);
  const date = new Date(yyyy, mm - 1, dd);
  if (date.getFullYear() !== yyyy || date.getMonth() !== mm - 1 || date.getDate() !== dd) {
    throw new Error(`parseV1Date: fecha inv\xE1lida (${input})`);
  }
  return date;
}

// src/query/try-collapse-to-prefix.ts
function tryCollapseToPrefix(values, minPrefixLength = 2) {
  if (values.length === 0) return null;
  if (values.length === 1) return null;
  const first = values[0];
  if (first === void 0 || first.length === 0) return null;
  let prefix = first;
  for (let i = 1; i < values.length; i += 1) {
    const candidate = values[i];
    if (candidate === void 0) return null;
    let j = 0;
    const maxLen = Math.min(prefix.length, candidate.length);
    while (j < maxLen && prefix.charCodeAt(j) === candidate.charCodeAt(j)) {
      j += 1;
    }
    prefix = prefix.slice(0, j);
    if (prefix.length < minPrefixLength) return null;
  }
  return prefix.length >= minPrefixLength ? prefix : null;
}

// src/query/parse-response.ts
function parseFocusViewResponse(raw) {
  if (typeof raw !== "object" || raw === null) {
    throw new Error("parseFocusViewResponse: el payload no es objeto");
  }
  const body = raw;
  const data = body.data;
  if (!Array.isArray(data)) {
    throw new Error("parseFocusViewResponse: data ausente o no es array");
  }
  const rows = [];
  const row_meta = [];
  for (const item of data) {
    if (typeof item !== "object" || item === null) continue;
    const attrs = item.attributes;
    if (attrs !== void 0 && typeof attrs === "object" && attrs !== null) {
      rows.push(attrs);
    } else {
      rows.push(item);
    }
    row_meta.push({
      id: typeof item.id === "string" ? item.id : "",
      type: typeof item.type === "string" ? item.type : ""
    });
  }
  const metaRaw = body.meta ?? {};
  const total = coerceNumber(metaRaw.total) ?? rows.length;
  const found_rows = coerceNumber(metaRaw.found_rows) ?? total;
  return { rows, meta: { total, found_rows }, row_meta };
}
function coerceNumber(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

// src/expressions/context.ts
function nowOf(ctx) {
  return ctx.now ?? /* @__PURE__ */ new Date();
}

// src/expressions/functions.ts
function expectNumber(value, fn, position) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  throw new Error(
    `${fn}: arg #${position} debe ser n\xFAmero (recibido ${typeof value}: ${String(value)})`
  );
}
function expectDate(value, fn, position) {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  if (typeof value === "string") {
    const dmy = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    if (dmy !== null) {
      return new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    }
    const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (iso !== null) {
      return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    }
    const ymd = value.match(/^(\d{4})(\d{2})(\d{2})$/);
    if (ymd !== null) {
      return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    }
  }
  throw new Error(
    `${fn}: arg #${position} debe ser Date o string de fecha (recibido ${typeof value}: ${String(value)})`
  );
}
function startOfDay(d) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}
function startOfMonth(d, monthOffset = 0) {
  return new Date(d.getFullYear(), d.getMonth() + monthOffset, 1);
}
function endOfMonth(d, monthOffset = 0) {
  return new Date(d.getFullYear(), d.getMonth() + monthOffset + 1, 0);
}
function startOfYear(d, yearOffset = 0) {
  return new Date(d.getFullYear() + yearOffset, 0, 1);
}
function startOfWeek(d) {
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
}
function daysBetween(a, b) {
  const aMs = startOfDay(a).getTime();
  const bMs = startOfDay(b).getTime();
  return Math.round((bMs - aMs) / (24 * 60 * 60 * 1e3));
}
var CANONICAL_FUNCTION_NAMES = [
  "$today",
  "$now",
  "$startOfDay",
  "$startOfMonth",
  "$endOfMonth",
  "$startOfYear",
  "$truncMonth",
  "$truncWeek",
  "$concat",
  "$daysBetween",
  "$subtract",
  "$divide"
];
var FUNCTIONS = {
  $today: (_args, ctx) => startOfDay(nowOf(ctx)),
  $now: (_args, ctx) => nowOf(ctx),
  $startOfDay: (args, ctx) => {
    if (args.length === 0) return startOfDay(nowOf(ctx));
    return startOfDay(expectDate(args[0], "$startOfDay", 0));
  },
  $startOfMonth: (args, ctx) => {
    if (args.length === 0) return startOfMonth(nowOf(ctx));
    if (args.length === 1 && typeof args[0] === "number") {
      return startOfMonth(nowOf(ctx), args[0]);
    }
    return startOfMonth(expectDate(args[0], "$startOfMonth", 0));
  },
  $endOfMonth: (args, ctx) => {
    if (args.length === 0) return endOfMonth(nowOf(ctx));
    if (args.length === 1 && typeof args[0] === "number") {
      return endOfMonth(nowOf(ctx), args[0]);
    }
    return endOfMonth(expectDate(args[0], "$endOfMonth", 0));
  },
  $startOfYear: (args, ctx) => {
    if (args.length === 0) return startOfYear(nowOf(ctx));
    if (args.length === 1 && typeof args[0] === "number") {
      return startOfYear(nowOf(ctx), args[0]);
    }
    return startOfYear(expectDate(args[0], "$startOfYear", 0));
  },
  $truncMonth: (args, ctx) => {
    if (args.length === 0) return startOfMonth(nowOf(ctx));
    return startOfMonth(expectDate(args[0], "$truncMonth", 0));
  },
  $truncWeek: (args, ctx) => {
    if (args.length === 0) return startOfWeek(nowOf(ctx));
    return startOfWeek(expectDate(args[0], "$truncWeek", 0));
  },
  $concat: (args) => args.map((v) => v === null || v === void 0 ? "" : String(v)).join(""),
  $daysBetween: (args) => {
    if (args.length !== 2) {
      throw new Error("$daysBetween: aridad 2 esperada");
    }
    const a = expectDate(args[0], "$daysBetween", 0);
    const b = expectDate(args[1], "$daysBetween", 1);
    return daysBetween(a, b);
  },
  $subtract: (args) => {
    if (args.length !== 2) {
      throw new Error("$subtract: aridad 2 esperada");
    }
    return expectNumber(args[0], "$subtract", 0) - expectNumber(args[1], "$subtract", 1);
  },
  $divide: (args) => {
    if (args.length !== 2) {
      throw new Error("$divide: aridad 2 esperada");
    }
    const num = expectNumber(args[0], "$divide", 0);
    const den = expectNumber(args[1], "$divide", 1);
    if (den === 0) return null;
    return num / den;
  }
};
var FUNCTION_NAMES = CANONICAL_FUNCTION_NAMES;
var NULLARY_ALIASES = /* @__PURE__ */ new Set(["$today", "$now"]);

// src/expressions/parser.ts
var IDENT_RE = /^[a-zA-Z_][a-zA-Z0-9_]*/;
var NUMBER_RE = /^-?\d+(\.\d+)?/;
var ParseError = class extends Error {
  constructor(message, position) {
    super(`${message} (pos ${position})`);
    this.position = position;
  }
  position;
};
var Parser = class {
  constructor(src) {
    this.src = src;
  }
  src;
  pos = 0;
  parse() {
    this.skipWs();
    const node = this.parseExpr();
    this.skipWs();
    if (this.pos < this.src.length) {
      throw new ParseError(`token inesperado "${this.src.slice(this.pos)}"`, this.pos);
    }
    return node;
  }
  parseExpr() {
    this.skipWs();
    if (this.pos >= this.src.length) {
      throw new ParseError("expresi\xF3n vac\xEDa", this.pos);
    }
    const c = this.src[this.pos];
    if (c === "$") return this.parseDollar();
    if (c === '"') return this.parseString('"');
    if (c === "'") return this.parseString("'");
    if (c === "-" || typeof c === "string" && c >= "0" && c <= "9") {
      return this.parseNumber();
    }
    const m = this.src.slice(this.pos).match(IDENT_RE);
    if (m === null) {
      throw new ParseError(`token inesperado "${c}"`, this.pos);
    }
    const name = m[0];
    if (name === "null") {
      this.pos += 4;
      return { type: "NullLiteral" };
    }
    this.pos += name.length;
    return { type: "Identifier", name };
  }
  parseDollar() {
    this.pos += 1;
    const m = this.src.slice(this.pos).match(IDENT_RE);
    if (m === null) {
      throw new ParseError('se esperaba identificador despu\xE9s de "$"', this.pos);
    }
    const name = m[0];
    this.pos += name.length;
    if (name === "user") return this.parseUserPath();
    this.skipWs();
    if (this.src[this.pos] === "(") {
      this.pos += 1;
      const args = this.parseArgs();
      return { type: "Call", name: `$${name}`, args };
    }
    return { type: "Call", name: `$${name}`, args: [] };
  }
  parseUserPath() {
    const path = [];
    while (this.src[this.pos] === ".") {
      this.pos += 1;
      const m = this.src.slice(this.pos).match(IDENT_RE);
      if (m === null) {
        throw new ParseError('se esperaba identificador despu\xE9s de "."', this.pos);
      }
      path.push(m[0]);
      this.pos += m[0].length;
    }
    if (path.length === 0) {
      throw new ParseError("$user requiere al menos un segmento ($user.X)", this.pos);
    }
    return { type: "UserPath", path };
  }
  parseArgs() {
    const args = [];
    this.skipWs();
    if (this.src[this.pos] === ")") {
      this.pos += 1;
      return args;
    }
    args.push(this.parseExpr());
    this.skipWs();
    while (this.src[this.pos] === ",") {
      this.pos += 1;
      args.push(this.parseExpr());
      this.skipWs();
    }
    if (this.src[this.pos] !== ")") {
      throw new ParseError('se esperaba ")"', this.pos);
    }
    this.pos += 1;
    return args;
  }
  parseString(quote) {
    this.pos += 1;
    let value = "";
    while (this.pos < this.src.length && this.src[this.pos] !== quote) {
      if (this.src[this.pos] === "\\" && this.pos + 1 < this.src.length) {
        value += this.src[this.pos + 1];
        this.pos += 2;
      } else {
        value += this.src[this.pos];
        this.pos += 1;
      }
    }
    if (this.src[this.pos] !== quote) {
      throw new ParseError(`literal string sin cerrar (${quote})`, this.pos);
    }
    this.pos += 1;
    return { type: "StringLiteral", value };
  }
  parseNumber() {
    const m = this.src.slice(this.pos).match(NUMBER_RE);
    if (m === null) {
      throw new ParseError("se esperaba n\xFAmero", this.pos);
    }
    this.pos += m[0].length;
    return { type: "NumberLiteral", value: Number(m[0]) };
  }
  skipWs() {
    while (this.pos < this.src.length && /\s/.test(this.src[this.pos] ?? "")) {
      this.pos += 1;
    }
  }
};
function parseExpression(src) {
  return new Parser(src).parse();
}

// src/expressions/eval.ts
function isFunctionName(name) {
  return CANONICAL_FUNCTION_NAMES.includes(name);
}
function evalAst(node, ctx) {
  switch (node.type) {
    case "NumberLiteral":
      return node.value;
    case "StringLiteral":
      return node.value;
    case "NullLiteral":
      return null;
    case "Identifier": {
      if (ctx.row !== void 0 && Object.hasOwn(ctx.row, node.name)) {
        return ctx.row[node.name];
      }
      if (ctx.params !== void 0 && Object.hasOwn(ctx.params, node.name)) {
        return ctx.params[node.name];
      }
      return void 0;
    }
    case "UserPath": {
      const user = ctx.user;
      if (user === void 0) return void 0;
      let cursor = user;
      for (const segment of node.path) {
        if (cursor === null || cursor === void 0) return void 0;
        if (typeof cursor !== "object") return void 0;
        const obj = cursor;
        if (!Object.hasOwn(obj, segment)) return void 0;
        cursor = obj[segment];
      }
      return cursor;
    }
    case "Call": {
      if (!isFunctionName(node.name)) {
        throw new Error(`EXPR_UNKNOWN_FN: funci\xF3n desconocida ${node.name}`);
      }
      const args = node.args.map((arg) => evalAst(arg, ctx));
      return FUNCTIONS[node.name](args, ctx);
    }
  }
}
function evalExpression(src, ctx = {}) {
  const ast = parseExpression(src);
  return evalAst(ast, ctx);
}
function isDynamicExpression(value) {
  return typeof value === "string" && value.startsWith("$");
}

// src/expressions/validate.ts
var KNOWN_FUNCTIONS = new Set(CANONICAL_FUNCTION_NAMES);
function validateExpression(src) {
  let ast;
  try {
    ast = parseExpression(src);
  } catch (err) {
    return {
      ok: false,
      issues: [
        {
          kind: "syntax",
          message: err instanceof Error ? err.message : String(err)
        }
      ]
    };
  }
  const functions_used = /* @__PURE__ */ new Set();
  const identifiers_used = /* @__PURE__ */ new Set();
  const unknown = [];
  walk(ast, (node) => {
    if (node.type === "Call") {
      functions_used.add(node.name);
      if (!KNOWN_FUNCTIONS.has(node.name)) {
        unknown.push(node.name);
      }
    } else if (node.type === "Identifier") {
      identifiers_used.add(node.name);
    }
  });
  if (unknown.length > 0) {
    return {
      ok: false,
      issues: unknown.map((name) => ({
        kind: "unknown_function",
        message: `funci\xF3n desconocida: ${name}`
      }))
    };
  }
  return {
    ok: true,
    functions_used: [...functions_used],
    identifiers_used: [...identifiers_used]
  };
}
function walk(node, visit) {
  visit(node);
  if (node.type === "Call") {
    for (const arg of node.args) walk(arg, visit);
  }
}

// src/validator/types.ts
var emptyResult = { ok: true, issues: [] };
function mergeResults(...parts) {
  const issues = parts.flatMap((r) => r.issues);
  const ok = issues.every((i) => i.severity !== "error");
  return { ok, issues };
}

// src/validator/rules/cross-references.ts
function checkCrossReferences(t) {
  const issues = [];
  if (t.kind !== "report") return issues;
  const paramNames = new Set(t.parameters.map((p) => p.name));
  const columnKeys = new Set(t.columns.map((c) => c.key));
  const semiJoinNames = /* @__PURE__ */ new Set();
  if (t.semi_joins !== void 0) {
    for (const [i, sj] of t.semi_joins.entries()) {
      if (semiJoinNames.has(sj.name)) {
        issues.push({
          rule: "cross-references",
          severity: "error",
          message: `Nombre de semi_join duplicado: "${sj.name}".`,
          path: `/semi_joins/${i}/name`
        });
      }
      semiJoinNames.add(sj.name);
    }
  }
  for (const [i, mapping] of t.filter_mapping.entries()) {
    if (!paramNames.has(mapping.param)) {
      issues.push({
        rule: "cross-references",
        severity: "error",
        message: `filter_mapping[${i}].param "${mapping.param}" no existe en parameters.`,
        path: `/filter_mapping/${i}/param`
      });
    }
    if (mapping.via_semi_join !== void 0 && !semiJoinNames.has(mapping.via_semi_join)) {
      issues.push({
        rule: "cross-references",
        severity: "error",
        message: `filter_mapping[${i}].via_semi_join "${mapping.via_semi_join}" no existe en semi_joins.`,
        path: `/filter_mapping/${i}/via_semi_join`
      });
    }
  }
  if (t.default_view !== void 0) {
    const view = t.default_view;
    if (view.group_by !== void 0) {
      for (const [i, key] of view.group_by.entries()) {
        if (!columnKeys.has(key)) {
          issues.push({
            rule: "cross-references",
            severity: "error",
            message: `default_view.group_by[${i}] "${key}" no existe en columns.`,
            path: `/default_view/group_by/${i}`
          });
        }
      }
    }
    if (view.metrics !== void 0) {
      for (const [i, metric] of view.metrics.entries()) {
        if (!columnKeys.has(metric.column)) {
          issues.push({
            rule: "cross-references",
            severity: "error",
            message: `default_view.metrics[${i}].column "${metric.column}" no existe en columns.`,
            path: `/default_view/metrics/${i}/column`
          });
        }
      }
    }
    if (view.sort !== void 0) {
      for (const [i, sort] of view.sort.entries()) {
        if (!columnKeys.has(sort.column)) {
          issues.push({
            rule: "cross-references",
            severity: "error",
            message: `default_view.sort[${i}].column "${sort.column}" no existe en columns.`,
            path: `/default_view/sort/${i}/column`
          });
        }
      }
    }
  }
  return issues;
}

// src/validator/rules/dashboard-widgets.ts
function checkDashboardWidgets(t, knownTemplateCodes) {
  const issues = [];
  if (t.kind !== "dashboard") return issues;
  const seenIds = /* @__PURE__ */ new Set();
  for (const [i, widget] of t.layout.widgets.entries()) {
    if (seenIds.has(widget.id)) {
      issues.push({
        rule: "dashboard-widgets",
        severity: "error",
        message: `Widget id duplicado: "${widget.id}".`,
        path: `/layout/widgets/${i}/id`
      });
    }
    seenIds.add(widget.id);
    if (knownTemplateCodes !== void 0 && !knownTemplateCodes.has(widget.template_code)) {
      issues.push({
        rule: "dashboard-widgets",
        severity: "error",
        message: `Widget "${widget.id}" referencia template_code "${widget.template_code}" no conocido.`,
        path: `/layout/widgets/${i}/template_code`
      });
    }
    const totalCols = t.layout.columns;
    const right = widget.position.x + widget.position.w;
    if (right > totalCols) {
      issues.push({
        rule: "dashboard-widgets",
        severity: "error",
        message: `Widget "${widget.id}" excede el ancho del dashboard (x=${widget.position.x} + w=${widget.position.w} > columns=${totalCols}).`,
        path: `/layout/widgets/${i}/position`
      });
    }
  }
  return issues;
}

// src/validator/rules/data-source-fields.ts
function checkDataSourceFields(t) {
  const issues = [];
  if (t.kind !== "report") return issues;
  const includes = t.data_source.includes ?? [];
  const aliasesSoFar = /* @__PURE__ */ new Set();
  for (const [i, inc] of includes.entries()) {
    if (inc.local.includes(".")) {
      const prefix = inc.local.slice(0, inc.local.indexOf("."));
      if (!aliasesSoFar.has(prefix)) {
        issues.push({
          rule: "data-source-fields",
          severity: "error",
          message: `includes[${i}].local "${inc.local}" referencia alias "${prefix}" que no se ha declarado previamente.`,
          path: `/data_source/includes/${i}/local`,
          suggestion: "Reordenar los includes para que el alias se declare antes de su uso."
        });
      }
    }
    if (inc.alias !== void 0) aliasesSoFar.add(inc.alias);
  }
  const order = t.data_source.default_order;
  if (order !== void 0 && /^\s*(asc|desc)\s*$/i.test(order)) {
    issues.push({
      rule: "data-source-fields",
      severity: "warning",
      message: `data_source.default_order "${order}" parece incompleto (s\xF3lo direcci\xF3n, sin campo).`,
      path: "/data_source/default_order"
    });
  }
  return issues;
}

// src/validator/rules/drilldown-target.ts
function checkDrilldownTarget(t, knownTemplateCodes) {
  const issues = [];
  if (t.kind !== "report" || t.interactions?.drilldown === void 0) return issues;
  const target = t.interactions.drilldown.to_template;
  if (knownTemplateCodes === void 0) {
    issues.push({
      rule: "drilldown-target",
      severity: "info",
      message: `drilldown apunta a "${target}"; el validador no recibi\xF3 la lista de plantillas conocidas para verificar su existencia.`,
      path: "/interactions/drilldown/to_template"
    });
    return issues;
  }
  if (!knownTemplateCodes.has(target)) {
    issues.push({
      rule: "drilldown-target",
      severity: "error",
      message: `drilldown.to_template "${target}" no existe en el cat\xE1logo conocido.`,
      path: "/interactions/drilldown/to_template"
    });
  }
  return issues;
}

// src/validator/rules/expressions-valid.ts
function collectExpressions(t) {
  const out = [];
  for (const [i, p] of t.parameters.entries()) {
    if ("default" in p && isDynamicExpression(p.default)) {
      out.push({ src: p.default, path: `/parameters/${i}/default` });
    }
  }
  if (t.kind === "report") {
    for (const [i, c] of t.columns.entries()) {
      if (c.derive !== void 0 && c.derive.length > 0) {
        out.push({ src: c.derive, path: `/columns/${i}/derive` });
      }
    }
  }
  for (const [i, m] of t.filter_mapping.entries()) {
    if (m.when !== void 0 && isDynamicExpression(m.when)) {
      out.push({ src: m.when, path: `/filter_mapping/${i}/when` });
    }
  }
  return out;
}
function checkExpressionsValid(t) {
  const issues = [];
  const expressions = collectExpressions(t);
  for (const { src, path } of expressions) {
    const result = validateExpression(src);
    if (!result.ok) {
      for (const issue of result.issues) {
        issues.push({
          rule: "expressions-valid",
          severity: "error",
          message: `Expresi\xF3n inv\xE1lida en \`${path}\` ("${src}"): ${issue.message}`,
          path
        });
      }
    }
  }
  return issues;
}

// src/validator/rules/i18n-required.ts
var RECOMMENDED_LOCALES = ["en", "ca"];
function checkI18nRequired(t) {
  const issues = [];
  for (const locale of RECOMMENDED_LOCALES) {
    if (!(locale in t.title)) {
      issues.push({
        rule: "i18n-required",
        severity: "warning",
        message: `Falta traducci\xF3n "${locale}" en title (s\xF3lo "es" presente).`,
        path: `/title/${locale}`,
        suggestion: `A\xF1adir title.${locale}.`
      });
    }
  }
  if (t.description !== void 0) {
    for (const locale of RECOMMENDED_LOCALES) {
      if (!(locale in t.description)) {
        issues.push({
          rule: "i18n-required",
          severity: "info",
          message: `Falta traducci\xF3n "${locale}" en description.`,
          path: `/description/${locale}`
        });
      }
    }
  }
  return issues;
}

// src/validator/rules/money-canonical.ts
var CANONICAL_MONEY_FIELDS = /* @__PURE__ */ new Set([
  // Líneas
  "importe_euro",
  "precio_euro",
  "preivi_euro",
  "coste_alm_euro",
  "coste_ule_euro",
  "coste_man_euro",
  // Cabeceras (sin IVA, recomendado)
  "imp_bru_euro",
  "imp_bas_euro_1",
  "imp_bas_euro_2",
  "imp_bas_euro_3",
  "imp_bas_euro_4",
  // Cartera / almacén
  "puc_euro",
  "valor_euro"
]);
var NON_CANONICAL_BUT_KNOWN = /* @__PURE__ */ new Set([
  "imp_fra_euro"
  // con IVA → warning si se agrega
]);
function checkMoneyCanonical(t) {
  const issues = [];
  if (t.kind !== "report") return issues;
  for (const [i, col] of t.columns.entries()) {
    if (col.type !== "money") continue;
    if (col.derive !== void 0) continue;
    if (col.key.includes(".")) continue;
    const hasAggregation = (col.aggregations?.length ?? 0) > 0;
    if (!hasAggregation) continue;
    if (CANONICAL_MONEY_FIELDS.has(col.key)) continue;
    if (NON_CANONICAL_BUT_KNOWN.has(col.key)) {
      issues.push({
        rule: "money-canonical",
        severity: "warning",
        message: `Columna money "${col.key}" usa un campo con IVA; las estad\xEDsticas suelen ir sin IVA.`,
        path: `/columns/${i}/key`,
        suggestion: "Considerar `imp_bru_euro` para informes y estad\xEDsticas."
      });
      continue;
    }
    issues.push({
      rule: "money-canonical",
      severity: "warning",
      message: `Columna money "${col.key}" no usa un campo can\xF3nico (importe_euro, precio_euro, imp_bru_euro, coste_alm_euro, ...).`,
      path: `/columns/${i}/key`,
      suggestion: "Revisar la lista de campos can\xF3nicos del contrato v5 \xA71."
    });
  }
  return issues;
}

// src/validator/rules/pagination-coherent.ts
function checkPaginationCoherent(t) {
  const issues = [];
  if (t.kind !== "report") return issues;
  const pag = t.data_source.pagination;
  const maxRows = t.data_source.max_rows;
  let backendPageSize;
  if (pag.mode === "page_number") backendPageSize = pag.page_size;
  if (pag.mode === "limit") backendPageSize = pag.limit;
  if (backendPageSize !== void 0 && maxRows > 0 && backendPageSize > maxRows) {
    issues.push({
      rule: "pagination-coherent",
      severity: "error",
      message: `pagination.page_size (${backendPageSize}) supera data_source.max_rows (${maxRows}).`,
      path: "/data_source/pagination"
    });
  }
  const viewPageSize = t.default_view?.page_size;
  if (viewPageSize !== void 0 && backendPageSize !== void 0 && viewPageSize > backendPageSize) {
    issues.push({
      rule: "pagination-coherent",
      severity: "warning",
      message: `default_view.page_size (${viewPageSize}) supera pagination.page_size (${backendPageSize}); el frontend no podr\xE1 mostrar m\xE1s filas que las cargadas.`,
      path: "/default_view/page_size"
    });
  }
  return issues;
}

// src/validator/rules/schema.ts
function checkSchema(input) {
  const result = ReportTemplateModelSchema.safeParse(input);
  if (result.success) {
    return { ok: true, template: result.data, issues: [] };
  }
  const issues = formatZodErrors(result.error);
  return { ok: false, issues };
}
function formatZodErrors(error) {
  return error.issues.map((issue) => ({
    rule: "schema",
    severity: "error",
    message: `${issue.message} (en \`${issue.path.join(".")}\`)`,
    path: `/${issue.path.join("/")}`
  }));
}

// src/validator/rules/semi-join-bounds.ts
function checkSemiJoinBounds(t) {
  const issues = [];
  if (t.kind !== "report" || t.semi_joins === void 0) return issues;
  for (const [i, sj] of t.semi_joins.entries()) {
    if (sj.mode === "fanout" && sj.cache_ttl_seconds === 0) {
      issues.push({
        rule: "semi-join-bounds",
        severity: "info",
        message: `semi_join "${sj.name}" con mode fanout y cache_ttl_seconds=0: cada ejecuci\xF3n repetir\xE1 las llamadas paralelas.`,
        path: `/semi_joins/${i}/cache_ttl_seconds`,
        suggestion: "Considerar cache_ttl_seconds > 0 para reducir llamadas redundantes."
      });
    }
    if (sj.mode === "exclude_postfilter" && sj.max_projected_ids >= 500) {
      issues.push({
        rule: "semi-join-bounds",
        severity: "info",
        message: `semi_join "${sj.name}" usa el cap m\xE1ximo (500) en mode exclude_postfilter; el motor lanzar\xE1 SEMI_JOIN_EXCLUDE_TOO_BROAD si se excede.`,
        path: `/semi_joins/${i}/max_projected_ids`
      });
    }
  }
  return issues;
}

// src/validator/validate-template.ts
function validateTemplate(input, options = {}) {
  const schemaResult = checkSchema(input);
  if (!schemaResult.ok) {
    return { ok: false, issues: schemaResult.issues };
  }
  const t = schemaResult.template;
  const issues = [];
  issues.push(...checkI18nRequired(t));
  issues.push(...checkMoneyCanonical(t));
  issues.push(...checkCrossReferences(t));
  issues.push(...checkExpressionsValid(t));
  issues.push(...checkSemiJoinBounds(t));
  issues.push(...checkPaginationCoherent(t));
  issues.push(...checkDrilldownTarget(t, options.knownTemplateCodes));
  issues.push(...checkDataSourceFields(t));
  issues.push(...checkDashboardWidgets(t, options.knownTemplateCodes));
  const ok = issues.every((i) => i.severity !== "error");
  return { ok, issues, template: t };
}

// src/index.ts
var SHARED_PACKAGE_NAME = "@quartup/reports-shared";
var CONTRACT_VERSION = "v5";

export { AggregationFnSchema, CANONICAL_FUNCTION_NAMES, CONTRACT_VERSION, ColumnDefSchema, ColumnTypeSchema, DashboardLayoutSchema, DashboardWidgetSchema, DynamicExpressionSchema, FOCUS_VIEWS, FUNCTIONS, FUNCTION_NAMES, FilterClauseSchema, FilterMappingSchema, FilterOpSchema, FocusViewDataSourceSchema, FocusViewSchema, InitFilterSchema, InteractionsSchema, LocaleStringSchema, MetricSchema, NULLARY_ALIASES, PaginationStrategySchema, ParameterDefSchema, ParameterTypeSchema, ReportTemplateModelSchema, SHARED_PACKAGE_NAME, SemiJoinDefSchema, SemiJoinFilterMappingSchema, SemiJoinModeSchema, SortClauseSchema, ViewIncludeSchema, ViewStateSchema, VisualizationKindSchema, buildApiV1Query, checkCrossReferences, checkDashboardWidgets, checkDataSourceFields, checkDrilldownTarget, checkExpressionsValid, checkI18nRequired, checkMoneyCanonical, checkPaginationCoherent, checkSchema, checkSemiJoinBounds, emptyResult, evalAst, evalExpression, formatV1Date, isDynamicExpression, mergeResults as mergeTemplateValidationResults, nowOf, parseExpression, parseFocusViewResponse, parseV1Date, tryCollapseToPrefix, validateExpression, validateTemplate };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map